import { Injectable } from '@angular/core';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { environment } from '@environments/environment';
import { AnalyticsEvent } from '@shared/enums/analytics-enums';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly isMobileApp = environment.mobileApp?.isMobileApp ?? false;
  private readonly isLocalHost = window.location.origin.includes('localhost');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async logEvent(name: string, params?: Record<string, any>): Promise<void> {
    if (!this.isMobileApp) return;
    try {
      await FirebaseAnalytics.logEvent({ name, params });
      console.info('analytics success');
    } catch (_error) {
      console.error('analytics error');
    }
  }

  async setUserProperty(name: string, value: string): Promise<void> {
    if (!this.isMobileApp) return;
    try {
      await FirebaseAnalytics.setUserProperty({ name, value });
      console.info('analytics success');
    } catch (_error) {
      console.error('analytics error');
    }
  }

  // Custom Dimensions
  async setPushNotificationsEnabled(enabled: boolean): Promise<void> {
    await this.setUserProperty(AnalyticsEvent.PUSH_NOTIFICATIONS_ENABLED, enabled.toString());
  }

  async setLoggedIn(status: boolean): Promise<void> {
    await this.setUserProperty(AnalyticsEvent.LOGGED_IN, status.toString());
  }

  async setBetterDealsAvailable(available: boolean): Promise<void> {
    await this.setUserProperty(AnalyticsEvent.BETTER_DEALS_AVAILABLE, available.toString());
  }

  // Notifications
  async notificationReceived(): Promise<void> {
    return this.logEvent(AnalyticsEvent.NOTIFICATION_RECEIVED);
  }

  async notificationClicked(): Promise<void> {
    return this.logEvent(AnalyticsEvent.NOTIFICATION_CLICKED);
  }

  // App opened at least once
  async setAppOpened(id: string): Promise<void> {
    await this.setUserProperty(AnalyticsEvent.APP_OPENED, id);
  }

  async trackPageView(path: string): Promise<void> {
    await this.logEvent('page_view', { page_path: path });
  }

  // LEGACY ANALYTICS --------------------------------------------------------------------------------------------------
  setLegacyWebAnalytics(): void {
    if (!this.isMobileApp && !this.isLocalHost) {
      this.injectGTM(environment.gtm);
    }
  }

  private injectGTM(gtmId: string): void {
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];
        w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;
        j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;

    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
              height="0" width="0"
              style="display:none;visibility:hidden"></iframe>
    `;
    document.body.appendChild(noscript);
  }
}
